import React from 'react';

import { isStCapacityCompany, TradeEnum } from '_lib/api/apiTypes.shim';

import { Box } from '_lib/components/Box/Box';
import { ExternalLinkOrDefault } from '_lib/components/ExternalLinkOrDefault/ExternalLinkOrDefault';
import { Stack } from '_lib/components/Stack/Stack';
import { Typography } from '_lib/components/Typography/Typography';
import { CompanyLabels } from '_lib/constants/company.constants';
import { HeaderBox } from '_lib/featureComponents/HeaderBox/HeaderBox';
import { HeaderTextBlock } from '_lib/featureComponents/HeaderTextBlock/HeaderTextBlock';
import { canOrCant, formatAddressTwoLine, formatPhone } from '_lib/utils/helpers';

import { CompanyContext, ZoneZipContext } from '../Company.slice';
import { getBoxStyleForCanIncludeFee, getBoxStyleForCanWaiveFee } from '../CompanyPane.styles';
import { CompanyScheduleUrlHeaderBox } from '../CompanyScheduleUrl/CompanyScheduleUrl';

export interface CompanyDetailsProps {
  companyContext: CompanyContext;
  zoneZipContext: ZoneZipContext | undefined;
}

export const CompanyDetails = (props: CompanyDetailsProps): React.ReactElement => {
  const { companyContext, zoneZipContext } = props;
  const { company } = companyContext;

  const trades = company.callCenter.trades.map((trade) => TradeEnum[trade]).join(', ');

  return (
    <Box sx={{ px: 3, py: 2 }}>
      <Stack direction='row' spacing={2} sx={{ justifyContent: 'flex-start' }}>
        <Stack spacing={2} useFlexGap sx={{ width: '50%' }}>
          <HeaderTextBlock headerText={company.name} text={formatAddressTwoLine(company)} />
          <HeaderTextBlock headerText='Cross Streets' text={company.callCenter.crossStreets} />
          <HeaderTextBlock headerText='Phone Number' text={formatPhone(company.phoneNumber)} />
          {!!company.applicantPhoneNumber && (
            <HeaderTextBlock headerText='Applicant Phone Number' text={formatPhone(company.applicantPhoneNumber)} />
          )}
          <HeaderTextBlock headerText='Vendor Phone Number' text={formatPhone(company.callCenter.vendorPhoneNumber)} />
          <HeaderTextBlock headerText='Fax Number' text={formatPhone(company.faxNumber)} />
          <HeaderBox headerText='Website'>
            <ExternalLinkOrDefault url={company.website} label={CompanyLabels.WEBSITE} />
          </HeaderBox>
          {!!company.applicantWebsite && (
            <HeaderBox headerText='Applicant Website'>
              <ExternalLinkOrDefault url={company.applicantWebsite} label={CompanyLabels.APPLICANT_WEBSITE} />
            </HeaderBox>
          )}
          <HeaderTextBlock headerText='Email' text={company.email} />
          <HeaderTextBlock headerText='CC Emails' text={company.emailSettings.callPilot.ccEmails} />
          <HeaderTextBlock headerText='BCC Emails' text={company.emailSettings.callPilot.bccEmails} />
          <HeaderTextBlock headerText='Plain Text Emails' text={company.emailSettings.callPilot.plainTextEmails} />
        </Stack>
        <Stack spacing={2} useFlexGap sx={{ width: '50%' }}>
          {!company.isScheduleEnabled && !isStCapacityCompany(company) && (
            <CompanyScheduleUrlHeaderBox companyContext={companyContext} zoneZipContext={zoneZipContext} />
          )}
          <HeaderTextBlock headerText='Membership Plan' text={company.callCenter.membershipPlan} />
          <HeaderTextBlock headerText='Business Hours' text={company.callCenter.businessHours} />
          <HeaderTextBlock headerText='Time Zone' text={company.timezone} />
          <HeaderTextBlock headerText='Owner/GM' text={company.callCenter.ownerName} />
          <HeaderTextBlock headerText='Related Organizations' text={company.callCenter.relatedOrganizations} />
          <HeaderTextBlock headerText='Service Type' text={company.callCenter.serviceType} />
          <HeaderTextBlock headerText='Trades' text={trades} />
          {company.isZoned ? (
            <>
              <HeaderTextBlock
                headerText='Company Zone Information'
                text={
                  zoneZipContext?.selectedZone
                    ? `${zoneZipContext.selectedZone.title} is the zone used for the phone call.`
                    : 'The company does utilize zones, but no zone was selected during the call.'
                }
              />
              <HeaderBox headerText='Zip Codes Serviced'>
                <ul>
                  {company.zones.map((zone) => (
                    <li key={zone.id}>
                      {zone.title}: {zone.zipCodes.join(', ')}
                    </li>
                  ))}
                </ul>
              </HeaderBox>
            </>
          ) : (
            <>
              <HeaderTextBlock headerText='Company Zone Information' text='The company does not utilize zones.' />
              <HeaderTextBlock headerText='Zip Codes Serviced' text={company.callCenter.zipCodesServiced.join(', ')} />
            </>
          )}
          <Typography component='p' sx={(theme) => getBoxStyleForCanIncludeFee(company, theme)}>
            {'Appointment fee '}
            <span style={{ fontWeight: 'bold' }}>{canOrCant(company.callCenter.canIncludeFee)}</span>
            {' be included with work'}
          </Typography>
          <Typography component='p' sx={(theme) => getBoxStyleForCanWaiveFee(company, theme)}>
            {'Appointment fee '}
            <span style={{ fontWeight: 'bold' }}>{canOrCant(company.callCenter.canWaiveFee)}</span>
            {' be waived '}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};
